import { PaginationType } from "../../Types/APITypes";
import API from "./API";

class SurveyAPI extends API {
  // Survey Questions Management
  static getAllQuestions = (params?: { active?: boolean; page?: number; per_page?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined) {
      queryParams.append('active', params.active.toString());
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.per_page) {
      queryParams.append('per_page', params.per_page.toString());
    }
    const queryString = queryParams.toString();
    return this.getRequest(
      `manage/surveys/questions${queryString ? `?${queryString}` : ''}`,
      true
    );
  };

  static getQuestionById = (id: number) => {
    return this.getRequest(`manage/surveys/questions/${id}`, true);
  };

  static createQuestion = (data: any) => {
    return this.postRequest('manage/surveys/questions', data, true);
  };

  static updateQuestion = (id: number, data: any) => {
    return this.putRequest(`manage/surveys/questions/${id}`, data, true);
  };

  static deleteQuestion = (id: number) => {
    return this.deleteRequest(`manage/surveys/questions/${id}`, {}, true);
  };

  static reorderQuestions = (questionIds: number[]) => {
    return this.postRequest('manage/surveys/questions/reorder', { question_ids: questionIds }, true);
  };

  static toggleQuestionActive = (id: number) => {
    return this.postRequest(`manage/surveys/questions/${id}/toggle-active`, {}, true);
  };

  // Survey Responses and Analytics
  static getAllResponses = (params?: {
    page?: number;
    per_page?: number;
    start_date?: string;
    end_date?: string;
    tutor_id?: number;
    student_id?: number;
    booking_id?: number;
    completed?: boolean;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.tutor_id) queryParams.append('tutor_id', params.tutor_id.toString());
    if (params?.student_id) queryParams.append('student_id', params.student_id.toString());
    if (params?.booking_id) queryParams.append('booking_id', params.booking_id.toString());
    if (params?.completed !== undefined) queryParams.append('completed', params.completed.toString());
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);

    const queryString = queryParams.toString();
    return this.getRequest(
      `manage/surveys/analytics/responses${queryString ? `?${queryString}` : ''}`,
      true
    );
  };

  static getResponseById = (id: number) => {
    return this.getRequest(`manage/surveys/analytics/responses/${id}`, true);
  };

  static getSurveyStatistics = (params?: {
    start_date?: string;
    end_date?: string;
    tutor_id?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.tutor_id) queryParams.append('tutor_id', params.tutor_id.toString());

    const queryString = queryParams.toString();
    return this.getRequest(
      `manage/surveys/analytics/statistics${queryString ? `?${queryString}` : ''}`,
      true
    );
  };

  static getTutorComparison = (params?: {
    start_date?: string;
    end_date?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    return this.getRequest(
      `manage/surveys/analytics/tutor-comparison${queryString ? `?${queryString}` : ''}`,
      true
    );
  };

  static getTrends = (params?: {
    start_date?: string;
    end_date?: string;
    question_id?: number;
    group_by?: 'day' | 'week' | 'month';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.question_id) queryParams.append('question_id', params.question_id.toString());
    if (params?.group_by) queryParams.append('group_by', params.group_by);

    const queryString = queryParams.toString();
    return this.getRequest(
      `manage/surveys/analytics/trends${queryString ? `?${queryString}` : ''}`,
      true
    );
  };

  static exportResponses = (params?: {
    start_date?: string;
    end_date?: string;
    tutor_id?: number;
    student_id?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.tutor_id) queryParams.append('tutor_id', params.tutor_id.toString());
    if (params?.student_id) queryParams.append('student_id', params.student_id.toString());

    const queryString = queryParams.toString();
    const url = `manage/surveys/analytics/export${queryString ? `?${queryString}` : ''}`;
    
    // For CSV export, we need to handle the response as blob
    return fetch(`${API.API_URL}${url}`, {
      method: 'GET',
      headers: API.getHeadersAuth(),
    });
  };
}

export default SurveyAPI;
